import React, { useCallback, useMemo, useState } from 'react';
import { Plus, Save, LayoutGrid, Pencil, X, Check, Minus, MousePointerClick } from 'lucide-react';
import { SCREENS_DATA, SEAT_CATEGORIES, SCREEN_SEAT_MAPS } from '../data/mockData';
import type { SeatRow, ScreenLayout } from '../data/mockData';
const getCategoryColor = (cat: string) => {
  const found = SEAT_CATEGORIES.find(c => c.id === cat);
  return found?.color || '#6B7280';
};
const getCategoryLabel = (cat: string) => {
  const found = SEAT_CATEGORIES.find(c => c.id === cat);
  return found?.label || cat;
};
type EditMode = 'select' | 'edit';
export function ScreenConfig() {
  const [activeScreenId, setActiveScreenId] = useState(SCREENS_DATA[0].id);
  const [selectedSeats, setSelectedSeats] = useState<string[]>([]);
  const [editingRow, setEditingRow] = useState<string | null>(null);
  const [editCategory, setEditCategory] = useState('');
  const [editPrice, setEditPrice] = useState('');
  const [mode, setMode] = useState<EditMode>('select');
  // Mutable seat map state — clone from source data
  const [seatMaps, setSeatMaps] = useState<ScreenLayout[]>(() => JSON.parse(JSON.stringify(SCREEN_SEAT_MAPS)));
  const activeScreen = SCREENS_DATA.find(s => s.id === activeScreenId) || SCREENS_DATA[0];
  const seatMap = seatMaps.find(m => m.screenId === activeScreenId);
  // Group rows by category sections
  const sections = useMemo(() => {
    if (!seatMap) return [];
    const result: {
      category: string;
      price: number;
      rows: SeatRow[];
    }[] = [];
    let current: {
      category: string;
      price: number;
      rows: SeatRow[];
    } | null = null;
    for (const row of seatMap.rows) {
      if (!current || current.category !== row.category || current.price !== row.price) {
        current = {
          category: row.category,
          price: row.price,
          rows: []
        };
        result.push(current);
      }
      current.rows.push(row);
    }
    return result;
  }, [seatMap]);
  const capacityBreakdown = useMemo(() => {
    if (!seatMap) return {};
    const breakdown: Record<string, {
      count: number;
      price: number;
    }> = {};
    for (const row of seatMap.rows) {
      const seatCount = row.seats.filter(s => s !== null).length;
      const key = `${row.category}-${row.price}`;
      if (!breakdown[key]) breakdown[key] = {
        count: 0,
        price: row.price
      };
      breakdown[key].count += seatCount;
    }
    return breakdown;
  }, [seatMap]);
  const totalSeats = useMemo(() => {
    if (!seatMap) return 0;
    return seatMap.rows.reduce((sum, row) => sum + row.seats.filter(s => s !== null).length, 0);
  }, [seatMap]);
  const toggleSeat = (seatId: string) => {
    if (mode !== 'select') return;
    setSelectedSeats(prev => prev.includes(seatId) ? prev.filter(id => id !== seatId) : [...prev, seatId]);
  };
  // --- Seat editing functions ---
  const updateSeatMap = useCallback((updater: (map: ScreenLayout) => ScreenLayout) => {
    setSeatMaps(prev => prev.map(m => m.screenId === activeScreenId ? updater(m) : m));
  }, [activeScreenId]);
  const makeSeatGap = (rowLabel: string, colIdx: number) => {
    updateSeatMap(map => ({
      ...map,
      rows: map.rows.map(row => {
        if (row.label !== rowLabel) return row;
        const newSeats = [...row.seats];
        newSeats[colIdx] = null;
        return {
          ...row,
          seats: newSeats
        };
      })
    }));
  };
  const makeGapSeat = (rowLabel: string, colIdx: number) => {
    updateSeatMap(map => ({
      ...map,
      rows: map.rows.map(row => {
        if (row.label !== rowLabel) return row;
        const newSeats = [...row.seats];
        // Find the next seat number to use
        const existingNums = row.seats.filter((s): s is number => s !== null);
        const maxNum = existingNums.length > 0 ? Math.max(...existingNums) : 0;
        // Try to infer the right number from neighbors
        const leftSeat = colIdx > 0 ? row.seats[colIdx - 1] : null;
        const rightSeat = colIdx < row.seats.length - 1 ? row.seats[colIdx + 1] : null;
        let newNum = maxNum + 1;
        if (leftSeat !== null && rightSeat !== null) {
          newNum = leftSeat + 1;
        } else if (leftSeat !== null) {
          newNum = leftSeat + 1;
        } else if (rightSeat !== null) {
          newNum = rightSeat - 1;
        }
        newSeats[colIdx] = newNum > 0 ? newNum : 1;
        return {
          ...row,
          seats: newSeats
        };
      })
    }));
  };
  const addSeatToEnd = (rowLabel: string) => {
    updateSeatMap(map => ({
      ...map,
      rows: map.rows.map(row => {
        if (row.label !== rowLabel) return row;
        const existingNums = row.seats.filter((s): s is number => s !== null);
        const maxNum = existingNums.length > 0 ? Math.max(...existingNums) : 0;
        return {
          ...row,
          seats: [...row.seats, maxNum + 1]
        };
      })
    }));
  };
  const removeSeatFromEnd = (rowLabel: string) => {
    updateSeatMap(map => ({
      ...map,
      rows: map.rows.map(row => {
        if (row.label !== rowLabel) return row;
        if (row.seats.length <= 1) return row;
        return {
          ...row,
          seats: row.seats.slice(0, -1)
        };
      })
    }));
  };
  const startEditRow = (rowLabel: string, row: SeatRow) => {
    setEditingRow(rowLabel);
    setEditCategory(row.category);
    setEditPrice(String(row.price));
  };
  const cancelEditRow = () => {
    setEditingRow(null);
    setEditCategory('');
    setEditPrice('');
  };
  const saveEditRow = () => {
    if (!editingRow) return;
    updateSeatMap(map => ({
      ...map,
      rows: map.rows.map(row => row.label === editingRow ? {
        ...row,
        category: editCategory as SeatRow['category'],
        price: Number(editPrice) || row.price
      } : row)
    }));
    setEditingRow(null);
  };
  return <div className="h-[calc(100vh-8rem)] flex flex-col md:flex-row gap-6" data-id="element-487">
      {/* Left Panel: Screen List */}
      <div className="w-full md:w-72 lg:w-80 flex flex-col bg-white rounded-xl border border-gray-200 soft-shadow overflow-hidden flex-shrink-0" data-id="element-488">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gray-50" data-id="element-489">
          <h2 className="font-bold text-gray-900" data-id="element-490">Screens</h2>
          <button className="flex items-center gap-1.5 text-xs font-medium text-white px-2.5 py-1.5 rounded-md hover:opacity-90 transition-opacity" style={{
          backgroundColor: 'var(--accent-color)'
        }} data-id="element-491">
            <Plus className="w-3.5 h-3.5" data-id="element-492" />
            Add Screen
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1" data-id="element-493">
          {SCREENS_DATA.map(screen => <button key={screen.id} onClick={() => {
          setActiveScreenId(screen.id);
          setSelectedSeats([]);
          setEditingRow(null);
        }} className={`w-full text-left p-3 rounded-lg border transition-all ${activeScreenId === screen.id ? 'border-accent bg-accent/5 ring-1 ring-accent/20' : 'border-transparent hover:bg-gray-50'}`} style={activeScreenId === screen.id ? {
          borderColor: 'var(--accent-color)',
          backgroundColor: 'color-mix(in srgb, var(--accent-color) 5%, transparent)'
        } : {}} data-id="element-494">
              <div className="flex items-center justify-between mb-1" data-id="element-495">
                <span className="font-semibold text-gray-900" data-id="element-496">
                  {screen.name}
                </span>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${screen.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`} data-id="element-497">
                  {screen.status}
                </span>
              </div>
              <div className="flex items-center justify-between text-xs text-gray-500" data-id="element-498">
                <span className="flex items-center gap-1" data-id="element-499">
                  <LayoutGrid className="w-3 h-3" data-id="element-500" />
                  {screen.type}
                </span>
                <span data-id="element-501">{screen.capacity} seats</span>
              </div>
            </button>)}
        </div>
      </div>

      {/* Right Panel: Seat Map */}
      <div className="flex-1 flex flex-col bg-white rounded-xl border border-gray-200 soft-shadow overflow-hidden min-w-0" data-id="element-502">
        {/* Toolbar */}
        <div className="p-4 border-b border-gray-200 bg-gray-50" data-id="element-503">
          <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-504">
            <div data-id="element-505">
              <h2 className="font-bold text-gray-900 flex items-center gap-2" data-id="element-506">
                {activeScreen.name}
                <span className="text-xs font-medium text-gray-500 bg-gray-200 px-2 py-0.5 rounded-full" data-id="element-507">
                  {activeScreen.type}
                </span>
              </h2>
              <div className="flex flex-wrap items-center gap-3 mt-2" data-id="element-508">
                <span className="text-xs font-semibold text-gray-700" data-id="element-509">
                  {totalSeats} seats
                </span>
                <span className="text-gray-300" data-id="element-510">|</span>
                {Object.entries(capacityBreakdown).map(([key, val]) => {
                const cat = key.split('-')[0];
                return <span key={key} className="flex items-center gap-1 text-[11px] text-gray-500" data-id="element-511">
                      <span className="w-2 h-2 rounded-full" style={{
                    backgroundColor: getCategoryColor(cat)
                  }} data-id="element-512" />
                      {getCategoryLabel(cat)}: {val.count}
                      <span className="text-gray-400" data-id="element-513">₹{val.price}</span>
                    </span>;
              })}
              </div>
            </div>
            <div className="flex items-center gap-2" data-id="element-514">
              {/* Mode Toggle */}
              <div className="flex items-center bg-gray-200 rounded-lg p-0.5" data-id="element-515">
                <button onClick={() => {
                setMode('select');
                setSelectedSeats([]);
              }} className={`flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold rounded-md transition-all ${mode === 'select' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-516">
                  <MousePointerClick className="w-3 h-3" data-id="element-517" />
                  Select
                </button>
                <button onClick={() => {
                setMode('edit');
                setSelectedSeats([]);
              }} className={`flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold rounded-md transition-all ${mode === 'edit' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-518">
                  <Pencil className="w-3 h-3" data-id="element-519" />
                  Edit Seats
                </button>
              </div>
              <div className="w-px h-5 bg-gray-300 mx-1" data-id="element-520" />
              <button className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors" data-id="element-521">
                Add Row
              </button>
              <button className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium text-white rounded-md hover:opacity-90 transition-opacity" style={{
              backgroundColor: 'var(--accent-color)'
            }} data-id="element-522">
                <Save className="w-3.5 h-3.5" data-id="element-523" />
                Save Map
              </button>
            </div>
          </div>
        </div>

        {/* Edit Mode Banner */}
        {mode === 'edit' && <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center gap-3" data-id="element-524">
            <Pencil className="w-3.5 h-3.5 text-amber-600" data-id="element-525" />
            <span className="text-xs font-medium text-amber-800" data-id="element-526">
              Edit Mode — Click a <strong data-id="element-527">seat</strong> to make it a gap · Click
              a <strong data-id="element-528">gap</strong> to add a seat · Use <strong data-id="element-529">+/−</strong> at
              row ends to add or remove seats
            </span>
          </div>}

        {/* Bulk Action Bar */}
        {selectedSeats.length > 0 && mode === 'select' && <div className="bg-blue-50 border-b border-blue-100 p-2.5 flex items-center justify-between px-4" data-id="element-530">
            <span className="text-sm font-medium text-blue-800" data-id="element-531">
              {selectedSeats.length} seats selected
            </span>
            <div className="flex items-center gap-2" data-id="element-532">
              <select className="text-xs border-gray-300 rounded-md py-1 pl-2 pr-6" data-id="element-533">
                <option data-id="element-534">Assign Category...</option>
                {SEAT_CATEGORIES.map(cat => <option key={cat.id} data-id="element-535">{cat.label}</option>)}
              </select>
              <button className="px-3 py-1 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-md transition-colors" data-id="element-536">
                Block Selected
              </button>
              <button onClick={() => setSelectedSeats([])} className="px-2 py-1 text-xs font-medium text-gray-500 hover:text-gray-700" data-id="element-537">
                Clear
              </button>
            </div>
          </div>}

        {/* Map Area */}
        <div className="flex-1 overflow-auto p-6 bg-[#F8FAFC]" data-id="element-538">
          {seatMap ? <div className="inline-block min-w-full" data-id="element-539">
              <div className="max-w-3xl mx-auto" data-id="element-540">
                {/* Screen indicator */}
                <div className="flex flex-col items-center mb-10" data-id="element-541">
                  <div className="w-64 h-6 bg-gradient-to-b from-blue-100 to-blue-50 rounded-b-[100%] border border-blue-200/50" data-id="element-542" />
                  <span className="text-[10px] font-medium text-gray-400 mt-1.5 tracking-wider uppercase" data-id="element-543">
                    All eyes this way please
                  </span>
                </div>

                {/* Seat Grid by Sections */}
                <div className="space-y-6" data-id="element-544">
                  {sections.map((section, sIdx) => <div key={sIdx} data-id="element-545">
                      <div className="text-center mb-3" data-id="element-546">
                        <span className="text-xs font-bold text-gray-700 tracking-wide" data-id="element-547">
                          ₹{section.price}{' '}
                          <span style={{
                      color: getCategoryColor(section.category)
                    }} data-id="element-548">
                            {getCategoryLabel(section.category).toUpperCase()}
                          </span>
                        </span>
                      </div>

                      <div className="space-y-1.5" data-id="element-549">
                        {section.rows.map(row => {
                    const isEditing = editingRow === row.label;
                    return <div key={row.label} className="flex items-center gap-1 group" data-id="element-550">
                              {/* Row label */}
                              <div className="w-7 text-xs font-bold text-gray-400 text-right flex-shrink-0" data-id="element-551">
                                {row.label}
                              </div>

                              {/* Remove seat from end (edit mode) */}
                              {mode === 'edit' && <button onClick={() => removeSeatFromEnd(row.label)} className="w-5 h-5 flex items-center justify-center rounded-full bg-red-50 text-red-400 hover:bg-red-100 hover:text-red-600 transition-colors flex-shrink-0 opacity-0 group-hover:opacity-100" title="Remove last seat" data-id="element-552">
                                  <Minus className="w-2.5 h-2.5" data-id="element-553" />
                                </button>}

                              {/* Seats */}
                              <div className="flex items-center gap-[3px] flex-1 justify-center" data-id="element-554">
                                {row.seats.map((seat, colIdx) => {
                          if (seat === null) {
                            // Gap cell
                            if (mode === 'edit') {
                              return <button key={`gap-${colIdx}`} onClick={() => makeGapSeat(row.label, colIdx)} className="w-[26px] h-[26px] rounded border-2 border-dashed border-gray-300 flex items-center justify-center hover:border-green-400 hover:bg-green-50 transition-colors group/gap" title="Click to add seat here" data-id="element-555">
                                          <Plus className="w-3 h-3 text-gray-300 group-hover/gap:text-green-500 transition-colors" data-id="element-556" />
                                        </button>;
                            }
                            return <div key={`gap-${colIdx}`} className="w-[26px] h-[26px]" data-id="element-557" />;
                          }
                          const seatId = `${row.label}${seat}`;
                          const isSelected = selectedSeats.includes(seatId);
                          if (mode === 'edit') {
                            return <button key={seatId} onClick={() => makeSeatGap(row.label, colIdx)} className="w-[26px] h-[26px] rounded-t-md rounded-b-sm text-[9px] font-medium flex items-center justify-center transition-all border border-gray-300 hover:border-red-400 hover:bg-red-50 relative group/seat" style={{
                              color: '#6B7280'
                            }} title={`${seatId} — Click to make gap`} data-id="element-558">
                                        <span className="group-hover/seat:opacity-0 transition-opacity" data-id="element-559">
                                          {seat}
                                        </span>
                                        <X className="w-3 h-3 text-red-500 absolute opacity-0 group-hover/seat:opacity-100 transition-opacity" data-id="element-560" />
                                      </button>;
                          }
                          return <button key={seatId} onClick={() => toggleSeat(seatId)} className={`w-[26px] h-[26px] rounded-t-md rounded-b-sm text-[9px] font-medium flex items-center justify-center transition-all border ${isSelected ? 'ring-2 ring-offset-1 ring-gray-800 scale-110 z-10 border-gray-800' : 'border-gray-300 hover:scale-105 hover:z-10 hover:border-gray-400'}`} style={{
                            backgroundColor: isSelected ? getCategoryColor(row.category) : '#fff',
                            color: isSelected ? '#fff' : '#6B7280',
                            borderColor: isSelected ? getCategoryColor(row.category) : undefined
                          }} title={`${seatId} — ${getCategoryLabel(row.category)} ₹${row.price}`} data-id="element-561">
                                      {seat}
                                    </button>;
                        })}
                              </div>

                              {/* Add seat to end (edit mode) */}
                              {mode === 'edit' && <button onClick={() => addSeatToEnd(row.label)} className="w-5 h-5 flex items-center justify-center rounded-full bg-green-50 text-green-400 hover:bg-green-100 hover:text-green-600 transition-colors flex-shrink-0 opacity-0 group-hover:opacity-100" title="Add seat to end" data-id="element-562">
                                  <Plus className="w-2.5 h-2.5" data-id="element-563" />
                                </button>}

                              {/* Row label right */}
                              <div className="w-7 text-xs font-bold text-gray-400 flex-shrink-0" data-id="element-564">
                                {row.label}
                              </div>

                              {/* Edit row button (category/price) */}
                              {!isEditing ? <button onClick={() => startEditRow(row.label, row)} className="w-6 h-6 flex items-center justify-center rounded text-gray-300 opacity-0 group-hover:opacity-100 hover:bg-gray-100 hover:text-gray-600 transition-all flex-shrink-0" title={`Edit Row ${row.label}`} data-id="element-565">
                                  <Pencil className="w-3 h-3" data-id="element-566" />
                                </button> : <div className="flex items-center gap-1 flex-shrink-0 ml-2" data-id="element-567">
                                  <select value={editCategory} onChange={e => setEditCategory(e.target.value)} className="text-[10px] border border-gray-300 rounded py-0.5 pl-1 pr-4 bg-white" data-id="element-568">
                                    {SEAT_CATEGORIES.filter(c => c.id !== 'accessible' && c.id !== 'couple').map(cat => <option key={cat.id} value={cat.id} data-id="element-569">
                                        {cat.label}
                                      </option>)}
                                  </select>
                                  <div className="flex items-center" data-id="element-570">
                                    <span className="text-[10px] text-gray-400 mr-0.5" data-id="element-571">
                                      ₹
                                    </span>
                                    <input type="number" value={editPrice} onChange={e => setEditPrice(e.target.value)} className="w-12 text-[10px] border border-gray-300 rounded py-0.5 px-1" data-id="element-572" />
                                  </div>
                                  <button onClick={saveEditRow} className="w-5 h-5 flex items-center justify-center rounded bg-green-100 text-green-700 hover:bg-green-200 transition-colors" data-id="element-573">
                                    <Check className="w-3 h-3" data-id="element-574" />
                                  </button>
                                  <button onClick={cancelEditRow} className="w-5 h-5 flex items-center justify-center rounded bg-gray-100 text-gray-500 hover:bg-gray-200 transition-colors" data-id="element-575">
                                    <X className="w-3 h-3" data-id="element-576" />
                                  </button>
                                </div>}
                            </div>;
                  })}
                      </div>
                    </div>)}
                </div>
              </div>
            </div> : <div className="flex items-center justify-center h-full text-gray-400 text-sm" data-id="element-577">
              No seat map configured for this screen
            </div>}
        </div>

        {/* Legend */}
        <div className="p-3 border-t border-gray-200 bg-white flex flex-wrap items-center justify-center gap-5" data-id="element-578">
          {SEAT_CATEGORIES.filter(c => c.id !== 'accessible' && c.id !== 'couple').map(cat => <div key={cat.id} className="flex items-center gap-1.5" data-id="element-579">
              <div className="w-3.5 h-3.5 rounded-sm border border-gray-200" style={{
            backgroundColor: cat.color
          }} data-id="element-580" />
              <span className="text-[11px] font-medium text-gray-600" data-id="element-581">
                {cat.label}
              </span>
            </div>)}
          <div className="flex items-center gap-1.5" data-id="element-582">
            <div className="w-3.5 h-3.5 rounded-sm border-2 border-gray-800 bg-white" data-id="element-583" />
            <span className="text-[11px] font-medium text-gray-600" data-id="element-584">
              Selected
            </span>
          </div>
          <div className="flex items-center gap-1.5" data-id="element-585">
            <div className="w-3.5 h-3.5 rounded-sm border-2 border-dashed border-gray-300" data-id="element-586" />
            <span className="text-[11px] font-medium text-gray-600" data-id="element-587">
              Gap / Aisle
            </span>
          </div>
        </div>
      </div>
    </div>;
}